import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { catchError, firstValueFrom, of } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { MeService } from '../services/me.service';

// Precedence: a pending invitation wins outright, then an active-tenant claim, then any
// membership (probed directly since the claim mapper can lag); only both-empty falls to onboarding.
export const needsTenantGuard: CanActivateChildFn = async () => {
  const authService = inject(AuthService);
  const meService = inject(MeService);
  const router = inject(Router);

  // Platform admins have no tenant of their own and must reach the shell regardless.
  if (authService.isPlatformAdmin()) {
    return true;
  }

  // Step 1 - pending invitations always win, regardless of claim or memberships.
  let inviteCallFailed = false;
  const invitations = await firstValueFrom(
    meService.listMyInvitations().pipe(
      catchError(() => {
        inviteCallFailed = true;
        return of([]);
      }),
    ),
  );
  if (invitations.length > 0) {
    return router.parseUrl('/invitations');
  }

  // Step 2 - fast path: trust the JWT's claim, but only once the invite check
  // succeeded - otherwise a stale claim could short-circuit past a pending invite.
  if (!inviteCallFailed && authService.claims().activeTenantId) {
    return true;
  }

  // Step 3 - claim missing → fall back to actual memberships.
  let tenantsCallFailed = false;
  const tenants = await firstValueFrom(
    meService.listMyTenants().pipe(
      catchError(() => {
        tenantsCallFailed = true;
        return of([]);
      }),
    ),
  );
  if (tenants.length > 0) {
    // Single-tenant users may be missing the claim - establish it so downstream
    // endpoints resolve tenant scope. Best-effort: on failure, single-membership auto-resolve is the safety net.
    if (tenants.length === 1 && !authService.claims().activeTenantId) {
      try {
        await authService.setActiveTenantClaim(tenants[0].id);
      } catch {
        /* silent - backend RequireTenantScope handles single-membership */
      }
    }
    return true;
  }

  // Step 4 - falls to /invitations by default (covers link-only invites); /login
  // only when both calls actually failed, i.e. the backend is unreachable.
  if (tenantsCallFailed && inviteCallFailed) {
    return router.parseUrl('/login');
  }
  return router.parseUrl('/invitations');
};
